import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { compareArrays, relativeTimeFormatter } from "../../lib/formatters";
import { useGlobalContext } from "../../GlobalContext";
import { getCompany, updateCompany } from "../../api/company";
import { toast } from "react-toastify";

const StringInput = ({
  id,
  name,
  message,
  value,
  setValue,
  isValid,
  onBlur,
  disabled,
}) => {
  const [touched, setTouched] = useState(false);
  return (
    <div className="relative mb-2">
      <input
        type="text"
        id={id}
        className="peer h-12 w-full appearance-none rounded border-2 px-3 py-2 text-base leading-tight text-gray-700 shadow placeholder:text-transparent focus:outline-none"
        name={id}
        placeholder={name}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={(e) => {
          onBlur(e);
          setTouched(true);
        }}
        disabled={disabled}
      />
      <label className="absolute left-0 top-3 ml-2 -translate-y-5 bg-white px-1 text-sm text-gray-500 duration-100 ease-linear">
        {name}
      </label>
      <p
        className={`${(isValid || !touched) && "invisible"} text-xs italic text-errorColor`}
      >
        {message}
      </p>
    </div>
  );
};

const CompanyTypeSelect = ({
  name,
  message,
  value,
  setValue,
  isValid,
  onBlur,
  disabled,
}) => {
  const handleCheckboxChange = (type) => {
    if (value.includes(type)) {
      setValue(value.filter((v) => v !== type));
    } else {
      setValue([...value, type]);
    }
  };
  const [touched, setTouched] = useState(false);

  return (
    <div className="mb-2">
      <div className="relative w-full border-2 shadow">
        <label className="p-2 text-sm font-medium text-gray-500">{name}</label>
        <div className="mt-2 flex space-x-4 p-2">
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              className="size-4 text-blue-600"
              checked={value.includes("brokerage")}
              onChange={() => handleCheckboxChange("brokerage")}
              onBlur={(e) => {
                onBlur(e);
                setTouched(true);
              }}
              disabled={disabled}
            />
            <span className="ml-2 text-sm text-gray-700">Brokerage</span>
          </label>
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              className="size-4 text-blue-600"
              checked={value.includes("supplier")}
              onChange={() => handleCheckboxChange("supplier")}
              onBlur={(e) => {
                onBlur(e);
                setTouched(true);
              }}
              disabled={disabled}
            />
            <span className="ml-2 text-sm text-gray-700">Supplier</span>
          </label>
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              className="size-4 text-blue-600"
              checked={value.includes("tpi")}
              onChange={() => handleCheckboxChange("tpi")}
              onBlur={(e) => {
                onBlur(e);
                setTouched(true);
              }}
              disabled={disabled}
            />
            <span className="ml-2 text-sm text-gray-700">TPI</span>
          </label>
        </div>
      </div>
      <p
        className={`text-xs italic text-errorColor ${(isValid || !touched) && "invisible"}`}
      >
        {message}
      </p>
    </div>
  );
};

const CompanyDetails = () => {
  const { state, setState } = useGlobalContext();
  const [company, setCompany] = useState(null);
  useEffect(() => {
    if (!state?.selectedCompany?.uuid) {
      return;
    }
    setCompany(state?.selectedCompany);
    setFormData({
      name: state?.selectedCompany?.name,
      pseudonym: state?.selectedCompany?.pseudonym,
      contact_email: state?.selectedCompany?.contact_email,
      website: state?.selectedCompany?.website,
      types: state?.selectedCompany?.types || [],
      contact_phone_number: state?.selectedCompany?.contact_phone_number,
      address_line_1: state?.selectedCompany?.address_line_1,
      address_line_2: state?.selectedCompany?.address_line_2,
      town: state?.selectedCompany?.town,
      county: state?.selectedCompany?.county,
      postcode: state?.selectedCompany?.postcode,
    });
  }, [state?.selectedCompany]);
  const [editing, setEditing] = useState(false);
  const [validation, setValidation] = useState({
    name: true,
    contact_email: true,
    types: true,
    contact_phone_number: true,
    address_line_1: true,
    town: true,
    county: true,
    postcode: true,
  });
  const [formData, setFormData] = useState({
    name: company?.name || "",
    pseudonym: company?.pseudonym || "",
    contact_email: company?.contact_email || "",
    website: company?.website || "",
    types: company?.types || [],
    contact_phone_number: company?.contact_phone_number || "",
    address_line_1: company?.address_line_1 || "",
    address_line_2: company?.address_line_2 || "",
    town: company?.town || "",
    county: company?.county || "",
    postcode: company?.postcode || "",
  });

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      const clientData = { ...formData };
      Object.keys(clientData).forEach((key) => {
        if (key === "types") {
          if (compareArrays(clientData.types, company.types)) {
            delete clientData.types;
          }
        } else if (clientData[key] === company[key]) {
          delete clientData[key];
        }
      });
      if (Object.keys(clientData).length > 0) {
        await updateCompany(company.uuid, clientData);
        const updatedCompany = await getCompany(company.uuid);
        setState((prevState) => ({
          ...prevState,
          selectedCompany: updatedCompany,
        }));
      }
      setEditing(false);
      toast.success("Company edited successfully");
    } catch (error) {
      console.error(error);
      toast.error("Failed to edit company");
    }
  };

  return (
    <>
      <h1 className="mb-8 text-2xl font-bold">Company Details</h1>
      <div className="max-w-lg">
        <form onSubmit={handleFormSubmit} noValidate>
          <StringInput
            id="name"
            name="Name"
            message="Please enter a valid company name."
            value={formData.name || ""}
            setValue={(value) => setFormData({ ...formData, name: value })}
            isValid={validation.name}
            onBlur={() =>
              setValidation({
                ...validation,
                name: formData.name.trim().length > 3,
              })
            }
            disabled={!editing}
          />
          <StringInput
            id="pseudonym"
            name="Pseudonym"
            message="N/A"
            value={formData.pseudonym || ""}
            setValue={(value) => setFormData({ ...formData, pseudonym: value })}
            isValid={true}
            onBlur={() => {}}
            disabled={!editing}
          />

          <StringInput
            id="website"
            name="Website"
            message="N/A"
            value={formData.website || ""}
            setValue={(value) => setFormData({ ...formData, website: value })}
            isValid={true}
            onBlur={() => {}}
            disabled={!editing}
          />
          <StringInput
            id="contact_email"
            name="Contact Email"
            message="Please enter a valid email address."
            value={formData.contact_email || ""}
            setValue={(value) =>
              setFormData({ ...formData, contact_email: value })
            }
            isValid={validation.contact_email}
            onBlur={() =>
              setValidation({
                ...validation,
                contact_email:
                  formData.contact_email.trim().length > 3 &&
                  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email),
              })
            }
            disabled={!editing}
          />
          <CompanyTypeSelect
            id="company_type"
            name="Company Type"
            message="Please select at least one company type."
            value={formData.types || ""}
            setValue={(types) => setFormData({ ...formData, types })}
            isValid={validation.types}
            onBlur={() =>
              setValidation({
                ...validation,
                types: formData.types.length > 0,
              })
            }
            disabled={!editing}
          />
          <StringInput
            id="contact_phone_number"
            name="Phone Number"
            message="Please enter a valid UK Phone Number."
            value={formData.contact_phone_number || ""}
            setValue={(value) =>
              setFormData({ ...formData, contact_phone_number: value })
            }
            isValid={validation.contact_phone_number}
            onBlur={() =>
              setValidation({
                ...validation,
                contact_phone_number:
                  formData.contact_phone_number.trim().length > 3,
              })
            }
            disabled={!editing}
          />
          <StringInput
            id="address_line_1"
            name="Address Line 1"
            message="Please enter the Company's address line 1."
            value={formData.address_line_1 || ""}
            setValue={(value) =>
              setFormData({ ...formData, address_line_1: value })
            }
            isValid={validation.address_line_1}
            onBlur={() =>
              setValidation({
                ...validation,
                address_line_1: formData.address_line_1.trim().length > 3,
              })
            }
            disabled={!editing}
          />
          <StringInput
            id="address_line_2"
            name="Address Line 2"
            message="Please enter the Company's address line 2."
            value={formData.address_line_2 || ""}
            setValue={(value) =>
              setFormData({ ...formData, address_line_2: value })
            }
            isValid={true}
            onBlur={() => {}}
            disabled={!editing}
          />
          <StringInput
            id="town"
            name="Town"
            message="Please enter the Company's town."
            value={formData.town}
            setValue={(value) => setFormData({ ...formData, town: value })}
            isValid={validation.town}
            onBlur={() =>
              setValidation({
                ...validation,
                town: formData.town.trim().length > 3,
              })
            }
            disabled={!editing}
          />
          <StringInput
            id="county"
            name="County"
            message="Please enter the Company's county."
            value={formData.county || ""}
            setValue={(value) => setFormData({ ...formData, county: value })}
            isValid={validation.county}
            onBlur={() =>
              setValidation({
                ...validation,
                county: formData.county.trim().length > 3,
              })
            }
            disabled={!editing}
          />
          <StringInput
            id="postcode"
            name="Postcode"
            message="Please enter a valid UK postcode."
            value={formData.postcode || ""}
            setValue={(value) => setFormData({ ...formData, postcode: value })}
            isValid={validation.postcode}
            onBlur={() =>
              setValidation({
                ...validation,
                postcode: formData.postcode.trim().length > 3,
              })
            }
            disabled={!editing}
          />
          <p className="text-sm italic text-gray-500">
            Created {relativeTimeFormatter(state?.selectedCompany?.created_at)},
            last updated{" "}
            {relativeTimeFormatter(state?.selectedCompany?.updated_at)}
          </p>
          <div className="flex justify-end">
            {editing ? (
              <button
                disabled={Object.values(validation).some((v) => !v)}
                className="h-12 rounded-md border border-transparent bg-primaryColor px-4 py-2 text-secondaryColor shadow-md transition-all hover:bg-slate-700 hover:shadow-lg focus:bg-slate-700 focus:shadow-none active:bg-slate-700 active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500 disabled:shadow-none"
                type="submit"
              >
                Save
              </button>
            ) : (
              <button
                className="h-12 rounded-md border border-transparent bg-primaryColor px-4 py-2 text-secondaryColor shadow-md transition-all hover:bg-slate-700 hover:shadow-lg focus:bg-slate-700 focus:shadow-none active:bg-slate-700 active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500 disabled:shadow-none"
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setEditing(true);
                }}
              >
                Edit Company
              </button>
            )}
          </div>
        </form>
      </div>
    </>
  );
};

PropTypes.CompanyDetails = {
  company: PropTypes.shape({
    uuid: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    types: PropTypes.arrayOf(PropTypes.string),
    website: PropTypes.string,
    contact_email: PropTypes.string.isRequired,
    contact_phone_number: PropTypes.string.isRequired,
    address_line_1: PropTypes.string.isRequired,
    address_line_2: PropTypes.string,
    town: PropTypes.string.isRequired,
    county: PropTypes.string.isRequired,
    postcode: PropTypes.string.isRequired,
    created_at: PropTypes.string.isRequired,
    updated_at: PropTypes.string.isRequired,
  }).isRequired,
};

export default CompanyDetails;
